import { useTranslation } from 'react-i18next';

import { Icons, IconType } from '../../../../constants/icons';
import { colorForStroke } from '../../../../constants/Theme/DefaultColors';
import { AppBar, Character, CharacterPreview } from '../../../Molecules';
import {
  MemoryHelperPanel,
  MemoryHelperQuestionPanel,
} from '../../../Organisms';

import styles from './GameMemoryHelperTemplate.module.css';

type GameMemoryHelperTemplateProps = {
  questionPanel: MemoryHelperQuestionPanel;
  currentStep: number;
  maximumStep: number;
  defaultStep: number;
  isUseTranslate: boolean;
  originalLanguage: string;
  translatedLanguage: string;
  originalAudioTts: string | null | undefined;
  translatedAudioTts: string | null | undefined;
  translationResult: string | undefined;
  currentBlankCharacter: Character | null | undefined;
  currentComponent: number;
  currentStrokeIndex: number;
  onSwitchTranslate: () => void;
  onClickLanguage: () => void;
  onClickShowAllStep: () => void;
  onClickAddStep: () => void;
  onClickMinusStep: () => void;
  onClickHideAllStep: () => void;
};

export const GameMemoryHelperTemplate = ({
  questionPanel,
  currentStep,
  maximumStep,
  defaultStep,
  isUseTranslate,
  originalLanguage,
  translatedLanguage,
  originalAudioTts,
  translatedAudioTts,
  translationResult,
  currentBlankCharacter,
  currentComponent,
  currentStrokeIndex,
  onSwitchTranslate,
  onClickLanguage,
  onClickHideAllStep,
  onClickMinusStep,
  onClickAddStep,
  onClickShowAllStep,
}: GameMemoryHelperTemplateProps) => {
  const { t } = useTranslation();

  const isDefaultStep = currentStep === defaultStep;
  const canMinusStep = currentStep > 1;
  const canAddStep = currentStep < maximumStep;

  return (
    <div className={styles.screen}>
      <AppBar title={t('Memory Helper')} />
      <div className={styles.bannerText}>
        {t(
          'Gradually hide parts of the content to help you memorize it more effectively.',
        )}
      </div>
      <div className={styles.content}>
        <MemoryHelperPanel
          questionPanel={questionPanel}
          isUseTranslate={isUseTranslate}
          originalLanguage={originalLanguage}
          translatedLanguage={translatedLanguage}
          originalAudioTts={originalAudioTts}
          translatedAudioTts={translatedAudioTts}
          translationResult={translationResult}
          isGameStart={false}
          onSwitchTranslate={onSwitchTranslate}
          onClickLanguage={onClickLanguage}
        />
        <div className={styles.emptyContainer} />
        {currentBlankCharacter !== undefined && (
          <div className={styles.characterPreviews}>
            <CharacterPreview
              character={currentBlankCharacter}
              size={200}
              getColor={(index, component) => {
                if (component < (currentComponent ?? 0)) {
                  return colorForStroke(component);
                }

                if (
                  component === currentComponent &&
                  index < currentStrokeIndex &&
                  currentBlankCharacter
                ) {
                  return colorForStroke(index);
                }

                return undefined;
              }}
            />
          </div>
        )}
      </div>
      <div className={styles.bottomBar}>
        <img
          className={`${styles.controlButton} ${
            canMinusStep ? '' : styles.controlDisableButton
          }`}
          src={Icons[IconType.BlueArrowAll]}
          onClick={canMinusStep ? onClickHideAllStep : undefined}
        />
        <img
          className={`${styles.controlButton} ${
            canMinusStep ? '' : styles.controlDisableButton
          }`}
          src={Icons[IconType.BlueMinus]}
          onClick={canMinusStep ? onClickMinusStep : undefined}
        />
        <div className={styles.bottomBarText}>
          <div
            className={styles.processText}
          >{`${currentStep} / ${maximumStep}`}</div>
          <div
            className={
              isDefaultStep ? styles.difficutDefaultText : styles.difficutText
            }
          >
            {t(isDefaultStep ? 'Difficulty (Default)' : 'Difficulty')}
          </div>
        </div>
        <img
          className={`${styles.controlButton} ${
            canAddStep ? '' : styles.controlDisableButton
          }`}
          src={Icons[IconType.RedAdd]}
          onClick={canAddStep ? onClickAddStep : undefined}
        />
        <img
          className={`${styles.controlButton} ${
            canAddStep ? '' : styles.controlDisableButton
          }`}
          src={Icons[IconType.RedArrowAll]}
          onClick={canAddStep ? onClickShowAllStep : undefined}
        />
      </div>
    </div>
  );
};
